"use client";

/**
 * AddToAbilityPicker — modal for adding a skill or agent to a user-owned
 * ability. Filters the marketplace catalog to items the caller has
 * already installed standalone (the server enforces invariant I1 with a
 * 409 install-first hint, but pre-filtering keeps the UI honest).
 *
 * Single component handles both modes (skill | agent) because the shape
 * is identical: pick from a list, call the API, dismiss.
 *
 * Spec: docs/superpowers/specs/2026-05-17-abilities-skills-agents-design.md §5g, §5i
 */

import { useEffect, useState } from "react";
import { listSkillsCatalog } from "@/lib/api/skills";
import { listAgentsCatalog } from "@/lib/api/agents";
import { isIconUrl } from "@/lib/abilities/icon";
import {
  addSkillToAbility,
  addAgentToAbility,
  AbilityMembershipError,
} from "@/lib/api/abilities";

type Mode = "skill" | "agent";

interface PickerItem {
  slug: string;
  name: string;
  description: string;
  icon?: string;
  color?: string;
}

export interface AddToAbilityPickerProps {
  mode: Mode;
  abilityId: string;
  /** Slugs already in the ability — used to dim+disable in the picker. */
  excludeSlugs: string[];
  open: boolean;
  onClose: () => void;
  /** Fired after a successful add so the parent can refetch the ability. */
  onAdded: (slug: string) => void;
}

export function AddToAbilityPicker({
  mode,
  abilityId,
  excludeSlugs,
  open,
  onClose,
  onAdded,
}: AddToAbilityPickerProps) {
  const [items, setItems] = useState<PickerItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setItems(null);
    setError(null);
    (async () => {
      try {
        const rows =
          mode === "skill"
            ? (await listSkillsCatalog()).filter(
                (s) => s.state === "installed" || s.state === "system"
              )
            : (await listAgentsCatalog()).filter(
                (a) => a.state === "installed" || a.state === "system"
              );
        if (cancelled) return;
        const exclude = new Set(excludeSlugs);
        const mapped: PickerItem[] = rows
          .filter((r) => !exclude.has(r.slug))
          .map((r) => ({
            slug: r.slug,
            name: r.name,
            description: r.description,
            icon: "icon" in r ? (r as any).icon : (r as any).avatar_url,
            color: (r as any).color,
          }));
        setItems(mapped);
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? "Failed to load");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, mode, excludeSlugs]);

  if (!open) return null;

  async function handlePick(slug: string) {
    setBusy(slug);
    setError(null);
    try {
      if (mode === "skill") await addSkillToAbility(abilityId, slug);
      else await addAgentToAbility(abilityId, slug);
      onAdded(slug);
      onClose();
    } catch (e) {
      if (e instanceof AbilityMembershipError) {
        // 409 install-first hint is the most actionable signal; surface raw.
        setError(e.serverMessage);
      } else {
        setError((e as Error)?.message ?? "Failed to add");
      }
    } finally {
      setBusy(null);
    }
  }

  const label = mode === "skill" ? "skill" : "agent";

  return (
    <div
      className="fixed inset-0 z-[71] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-label={`Add ${label} to ability`}
        className="w-full max-w-md max-h-[80vh] flex flex-col bg-bg-base rounded-16 border border-border-subtle shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-border-subtle">
          <h2 className="text-4 font-semibold text-fg-default">
            Add a {label}
          </h2>
          <p className="text-3 text-fg-muted mt-1">
            Pick from {label}s you have installed.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-2 py-2">
          {items === null && (
            <div className="px-3 py-8 text-3.5 text-fg-muted">Loading…</div>
          )}
          {items !== null && items.length === 0 && (
            <div className="px-3 py-8 text-3.5 text-fg-muted">
              No more {label}s to add. Install one from the marketplace first.
            </div>
          )}
          {items !== null && items.length > 0 && (
            <ul className="flex flex-col">
              {items.map((item) => (
                <li key={item.slug}>
                  <button
                    type="button"
                    onClick={() => handlePick(item.slug)}
                    disabled={busy !== null}
                    className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-12 hover:bg-bg-subtle disabled:opacity-50 transition-colors"
                  >
                    <div
                      className="w-8 h-8 overflow-hidden rounded-8 shrink-0 flex items-center justify-center text-3.5"
                      style={{ background: item.color ?? "var(--bg-muted)" }}
                    >
                      {isIconUrl(item.icon) ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={item.icon}
                          alt=""
                          className="ab-chip-ico__photo"
                        />
                      ) : (
                        item.icon ?? "·"
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-3.5 font-medium text-fg-default truncate">
                        {item.name}
                      </div>
                      {item.description && (
                        <div className="text-3 text-fg-muted line-clamp-1">
                          {item.description}
                        </div>
                      )}
                    </div>
                    {busy === item.slug && (
                      <span className="text-3 text-fg-muted">adding…</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {error && (
          <div className="px-5 py-3 border-t border-border-subtle text-3 text-error-fg">
            {error}
          </div>
        )}

        <div className="px-5 py-3 border-t border-border-subtle flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="text-3.5 text-fg-muted hover:text-fg-default px-3 py-1.5"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
